import { Injectable } from '@angular/core'
import { HttpClient, HttpParams } from '@angular/common/http'
import { BehaviorSubject, Observable } from 'rxjs'
import { map, tap } from 'rxjs/operators'
import { IUser } from '../interfaces/IUser'
import { ISearchUserResponse } from '../interfaces/ISearchUserResponse'
import { IAuthResponseData } from '../../../../auth/core/interfaces/IAuthResponseData'
import { FilterAccountsInterface } from '../interfaces/filter-accounts.interface'
import { SimpleAccountsResponseInterface } from '../interfaces/simple-accounts-response.interface'
import { ITradingAccount } from '../interfaces/ITradingAccount'
import { SendPushInterface } from '../interfaces/send-push.interface'
import { EnvironmentsHelperService } from '../../../../../../environments/core/services/environments-helper.service'
import { CURRENT_USER_STORAGE_KEY } from '../../../../auth/core/consts/storage-keys'
import { MARKETING_EMAILS } from '../../../../../shared/consts/marketing-emails.const'
import { RolesEnum } from '../enums/roles.enum'

@Injectable({ providedIn: 'root' })
export class UserService {
	public userDetail$: BehaviorSubject<IUser | null> = new BehaviorSubject<IUser | null>(null)

	get shortUserDetail(): IAuthResponseData {
		return JSON.parse(localStorage.getItem(CURRENT_USER_STORAGE_KEY))
	}

	constructor(
		private _http: HttpClient,
		private _environmentsHelperService: EnvironmentsHelperService,
	) {}

	public getCurrentUser(): Observable<IUser> {
		return this._http.get<IUser>(`${this._environmentsHelperService.authUrl}/api/v1/users/me`).pipe(
			tap((response: IUser) => {
				if (MARKETING_EMAILS.includes(response.email)) {
					response.roles = [RolesEnum.marketing]
				}
				this.userDetail$.next(response)
			}),
		)
	}

	public getSimpleAccounts(
		pageSize?: number,
		pageCount?: number,
		filterParams?: FilterAccountsInterface,
	): Observable<SimpleAccountsResponseInterface> {
		let params: HttpParams = new HttpParams()
		params = params.append('page_size', pageSize)
		params = params.append('page', pageCount ? pageCount : 1)
		params = params.append('sort_key', 'created_at')
		params = params.append('desc', true)

		if (filterParams) {
			Object.keys(filterParams).forEach((key: string) => {
				params = params.append(key, filterParams[key])
			})
		}

		return this._http.get<SimpleAccountsResponseInterface>(
			`${this._environmentsHelperService.authUrl}/api/v1/admin/users/simple`,
			{ params },
		)
	}

	public getUserById(id: number): Observable<IUser> {
		const params: HttpParams = new HttpParams().append('user_id', id)
		return this._http.get<IUser>(`${this._environmentsHelperService.authUrl}/api/v1/admin/users/detail`, { params })
	}

	public updateUserById(id: number, data: Partial<IUser> | any): Observable<IUser> {
		return this._http.patch<IUser>(`${this._environmentsHelperService.authUrl}/api/v1/admin/users/${id}`, data)
	}

	public searchUsers(value: string): Observable<ISearchUserResponse> {
		const params: HttpParams = new HttpParams().append('like', value.trim())
		return this._http
			.get<ISearchUserResponse>(`${this._environmentsHelperService.authUrl}/api/v1/admin/search/users`, {
				params,
			})
			.pipe(
				map((response: ISearchUserResponse) => ({
					...response,
					results: response.results.map((user) => {
						const full_name = `${user.first_name} ${user.last_name}`.trim()
						return {
							...user,
							full_name,
							extended_label: `${full_name} (${user.email})`,
							full_name_and_email: `${full_name} (${user.email})`,
						}
					}),
				})),
			)
	}

	public getTradingAccount(brokerId: string): Observable<ITradingAccount> {
		return this._http.get<ITradingAccount>(
			`${this._environmentsHelperService.apiUrl}/proxy_api/v1/alpaca/account_trading_data/${brokerId}/`,
		)
	}

	public deleteStatusPdt(brokerId: string): Observable<any> {
		return this._http.post(
			`${this._environmentsHelperService.apiUrl}/remove_pdt_status_for_account_once/${brokerId}/`,
			{},
		)
	}

	public syncOrders(users_ids: number[], is_refresh: boolean, is_save: boolean): Observable<any> {
		const params: HttpParams = new HttpParams()
			.append('users_ids', users_ids.join(','))
			.append('is_refresh', is_refresh)
			.append('is_save', is_save)

		return this._http.post(
			`${this._environmentsHelperService.apiUrl}/orders/sync_orders_for_users/`,
			{},
			{ params },
		)
	}

	public sendPush(data: SendPushInterface): Observable<any> {
		return this._http.post(`${this._environmentsHelperService.apiUrl}/notifications/send-push/`, data)
	}

	public getDepositInfo(userId: number): Observable<any> {
		return this._http.get<any>(`${this._environmentsHelperService.apiUrl}/deposits/v1/admin_get_deposit/${userId}/`)
	}
}
